import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { colorFor } from './weekColors'

const FADED_OPACITY = 0.12
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

function escapeHtml (s) {
  return String(s || '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}

/**
 * Mounts the Trace-mode polylines + circle markers onto the shared mapRef.
 * Re-renders when dayGroups, colorMode, crewColors, activeDay, hoverDay,
 * markerSize, or yard change.
 *
 * Phase 1: no A/B branching (all stops rendered solid).
 */
export default function WeekTraceLayer ({
  mapRef, dayGroups, yard, colorMode, crewColors,
  activeDay, hoverDay, markerSize = 12, onStopClick
}) {
  const layerRef = useRef(null)
  const rendererRef = useRef(null)

  // MapTab passes the Leaflet map as a raw state value (the parent's
  // `mapRef` is the result of `useState(null)` set via onMapLoad), but
  // tests pass a `{ current: null }` shaped object. Support both shapes.
  const map = mapRef && typeof mapRef.addLayer === 'function' ? mapRef : mapRef?.current

  // Create the canvas renderer + layer group once.
  useEffect(() => {
    if (!map) return
    rendererRef.current = L.canvas({ padding: 0.5 })
    layerRef.current = L.layerGroup().addTo(map)
    return () => {
      if (layerRef.current) map.removeLayer(layerRef.current)
      layerRef.current = null
      rendererRef.current = null
    }
  }, [map])

  // Re-render whenever inputs change.
  useEffect(() => {
    const layer = layerRef.current
    const renderer = rendererRef.current
    if (!layer || !renderer || !yard) return
    layer.clearLayers()

    const fadeFor = (g) => {
      const dimmed = activeDay != null && g.dayIndex !== activeDay
      const hovered = activeDay == null && hoverDay != null && g.dayIndex !== hoverDay
      return dimmed || hovered
    }

    for (const g of dayGroups) {
      const color = colorFor({ dayIndex: g.dayIndex, crewId: g.crewId }, colorMode, crewColors)
      const faded = fadeFor(g)
      const coords = g.geometry?.geometry?.coordinates
        ? g.geometry.geometry.coordinates.map(([lng, lat]) => [lat, lng])
        : [
            [yard.lat, yard.lng],
            ...g.stops.map(s => [s.lat, s.lng]),
            [yard.lat, yard.lng]
          ]
      L.polyline(coords, {
        renderer,
        color,
        weight: 2.5,
        opacity: faded ? FADED_OPACITY : 0.85,
        lineJoin: 'round',
        lineCap: 'round'
      }).addTo(layer)

      for (const stop of g.stops) {
        const marker = L.circleMarker([stop.lat, stop.lng], {
          renderer,
          radius: markerSize / 2,
          fillColor: color,
          fillOpacity: faded ? FADED_OPACITY : 0.95,
          color: '#ffffff',
          weight: 1.5,
          opacity: faded ? FADED_OPACITY : 1
        })
        marker.bindTooltip(`
  <div style="border-left:3px solid ${color};padding-left:6px;min-width:160px">
    <div style="font-weight:700;color:#1c2419">${escapeHtml(stop.label)}</div>
    ${stop.address ? `<div style="font-size:11px;color:#6f7a6b;margin-top:2px">${escapeHtml(stop.address)}</div>` : ''}
    <div style="font-size:11px;color:#6f7a6b;margin-top:4px">
      ${WEEKDAY_NAMES[g.dayIndex] || ''} · stop #${stop.seq || '?'}
      · ${stop.serviceMin || 0}m work
      · ${stop.driveMin || 0}m drive
    </div>
  </div>
`, { direction: 'top', offset: [0, -6], opacity: 0.95, sticky: false })
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e)
          if (onStopClick) onStopClick(stop)
        })
        marker.addTo(layer)
      }
    }
  }, [dayGroups, yard, colorMode, crewColors, activeDay, hoverDay, markerSize, onStopClick])

  return null
}
